/**
 * Workspace component – ag-grid React equivalent of Angular WorkspaceComponent.
 *
 * Core functionalities implemented:
 *  1. Receives workspace data (initialGridData) + ground data from parent
 *  2. Sets / formats data for ag-grid
 *  3. Fetches timeline config and builds column definitions
 *  4. Runs KPI calculators on initial load and on cell edits
 *  5. Emits cell changes back to parent via onWeekDataChanged
 *  6. Drag-select cells → count + sum in footer (via AppContext)
 *  7. Copy (Ctrl+C), Paste (Ctrl+V), Undo (Ctrl+Z) with clipboard service
 *  8. Right-click context menu "Copy cells to non-main market"
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    type ColDef,
    type ColGroupDef,
    type CellValueChangedEvent,
    type CellMouseDownEvent,
    type CellMouseOverEvent,
    type CellDoubleClickedEvent,
    type GridReadyEvent,
    type GridApi,
    type SizeColumnsToFitGridStrategy,
    themeQuartz,
    AllCommunityModule,
    ModuleRegistry,
    TooltipModule,
    CellSpanModule,
    ClientSideRowModelModule,
} from 'ag-grid-community';

import type {
    Market,
    WorkspaceData,
    DepartmentLevelKpiPlanModels,
    CalculatorConfig,
    PublishMetadataPerDepartment,
    SectionMetadataResponse,
} from '../../models';
import type { Department } from '../../models/organization';
import {
    Quarter as Q,
    KPI_SQ, KPI_SP, KPI_AI, KPI_SW,
    KPI_CORE, KPI_E, KPI_EXT, KPI_ART,
    KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND,
    KPI_UNKNOWN_GROUND, KPI_ORDERED_GROUND,
    PLANLEVEL_GROUND,
    TIME_PERIOD, TIME_WEEK, TIME_QUARTER,
} from '../../constants';
import {
    planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled,
    groundKpiCalculatorConfig_OrderedGroundEnabled,
} from '../../config/kpiCalculatorConfig';
import * as GridDataService from '../../services/gridDataService';
import * as CalculatorService from '../../services/calculatorService';
import { isExcitementKpiRow, isNonPrimaryBeforeSplit, isNonPrimaryEndingSeasonBeforeSplit, parseCellKey } from '../../helpers/gridUtilities';
import { GridSelectionHelper } from '../../helpers/gridSelectionHelper';
import { ClipboardService } from '../../services/clipboardService';
import { buildCopyToAllRegionsData, type KpiDataItem } from '../../services/copyArticleService';
// MultiSeasonTooltip double-click event (matches Angular DataTransferService)
import { MST_DOUBLE_CLICK_EVENT } from '../MultiSeasonTooltip/MultiSeasonTooltip';
import { useAppContext } from '../../context/AppContext';

import './Workspace.css';
import { Label, Icons, Tooltip, Spinner, Button, useAddToast } from '@hm-group/bads-react';

// Register ag-grid modules once
ModuleRegistry.registerModules([
    TooltipModule,
    AllCommunityModule,
    CellSpanModule,
    ClientSideRowModelModule,
]);

// ── Default KPI selections (used when no prop provided) ────────────
const DEFAULT_PLAN_KPIS = [KPI_CORE, KPI_E, KPI_EXT, KPI_ART];
const DEFAULT_GROUND_KPIS = [KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND, KPI_UNKNOWN_GROUND, KPI_ORDERED_GROUND];

// ── Props ──────────────────────────────────────────────────────────

export interface WorkspaceProps {
    initialGridData: WorkspaceData | null;
    groundRowData: DepartmentLevelKpiPlanModels[];
    readonlyPlan?: boolean;
    kpiConfig?: CalculatorConfig[];
    groundKpiConfig?: CalculatorConfig[];
    selectedPlanKpis?: string[];
    selectedGroundKpis?: string[];
    isMainMarket?: boolean;
    isStoreWorkspaceTab?: boolean;
    selectedDepartmentInfo?: Department | null;
    selectedRegions: Market[];
    /** Connected market codes (linked=false, non-main) – these rows should be read-only */
    connectedMarketCodes?: Set<number>;
    /** Main market code – used to look up data for connected markets */
    mainMarketCode?: number;
    isIncludeExcitementToggleChecked?: boolean;
    isShowGroundArticlesToggleChecked?: boolean;
    /** Fires when cell values change – parent should persist. */
    onWeekDataChanged?: (data: Map<string, { value: number; isLatestSeason: boolean }>) => void;
    /** Tooltip trigger counter – incremented to show multi-season tooltip */
    tooltipTrigger?: number;
    /** Callback when cell is double-clicked */
    onTooltipTriggered?: () => void;
    /** Flag to show published info */
    showPublishedInfo?: boolean;
    /** Department last published metadata */
    departmentLastPublishedInfo?: PublishMetadataPerDepartment;
    /** Section metadata with all departments' publish info */
    sectionLastPublishedInfo?: SectionMetadataResponse;
    /** Flag to show saving spinner */
    isSaving?: boolean;
    /** Callback when user triggers "copy cells to non-main market" from context menu */
    onCopyToAllRegions?: (kpiData: KpiDataItem[]) => void;
    /** Shared clipboard service instance (lifted from parent for cross-grid copy/paste) */
    clipboardService?: ClipboardService;
    /** Flag to show grid state changed recovery banner */
    isGridStateChanged?: boolean;
    /** Callback when user confirms reapplying saved changes after workspace reload */
    onConfirmReapply?: () => void;
}

// ── Theme ──────────────────────────────────────────────────────────

// NOTE: ag-grid's JS theme API cannot resolve CSS custom-property references
// (e.g. var(--bads-color-primary-30)) at initialisation time, so we use
// a concrete hex colour that matches --bads-color-primary-30.
const BORDER_COLOR = '#c8c8c8';

const myTheme = themeQuartz.withParams({
    fontSize: 9,
    headerFontSize: 10,
    spacing: 1.5,
    wrapperBorderRadius: 5,
    rowVerticalPaddingScale: 0.1,
    borderColor: BORDER_COLOR,
    headerRowBorder: { style: 'solid', width: 1.3, color: BORDER_COLOR },
    headerColumnBorder: { style: 'solid', width: 1.3, color: BORDER_COLOR },
    rowBorder: { style: 'solid', width: 1.3, color: BORDER_COLOR },
    columnBorder: { style: 'solid', width: 1.3, color: BORDER_COLOR },
});

const autoSizeStrategy: SizeColumnsToFitGridStrategy = {
    type: 'fitGridWidth',
    defaultMinWidth: 20,
    columnLimits: [
        { colId: 'marketShortName', minWidth: 30, maxWidth: 30 },
        { colId: 'kpi', minWidth: 85 },
    ],
};

// ── Component ──────────────────────────────────────────────────────

export function Workspace({
    initialGridData,
    groundRowData,
    readonlyPlan = false,
    kpiConfig: kpiConfigProp,
    groundKpiConfig: groundKpiConfigProp,
    selectedPlanKpis: selectedPlanKpisProp,
    selectedGroundKpis: selectedGroundKpisProp,
    isMainMarket: _isMainMarket = true,
    isStoreWorkspaceTab: _isStoreWorkspaceTab = true,
    selectedDepartmentInfo,
    selectedRegions,
    connectedMarketCodes,
    mainMarketCode,
    isIncludeExcitementToggleChecked = false,
    isShowGroundArticlesToggleChecked = false,
    onWeekDataChanged,
    onTooltipTriggered,
    showPublishedInfo = false,
    departmentLastPublishedInfo,
    sectionLastPublishedInfo,
    isSaving = false,
    onCopyToAllRegions,
    clipboardService: clipboardServiceProp,
    isGridStateChanged = false,
    onConfirmReapply,
}: Readonly<WorkspaceProps>) {

    // ── Context & BADS ───────────────────────────────────────────────
    const { setGridSelectionData } = useAppContext();
    const { addToast } = useAddToast();

    // ── Local state ──────────────────────────────────────────────────
    const [colDefs, setColDefs] = useState<(ColDef | ColGroupDef)[]>([]);
    const [rowData, setRowData] = useState<Record<string, unknown>[]>([]);
    const [timelineReady, setTimelineReady] = useState(false);

    // Context menu state
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
    const contextMenuCellRef = useRef<{ node: { data: Record<string, unknown> }; column: { getColId(): string }; value: unknown } | null>(null);

    const gridApiRef = useRef<GridApi | null>(null);
    const rowDataMapRef = useRef(new Map<string, Record<string, unknown>>());
    const cellValuesStateRef = useRef<{ field: string; kpi: string; value: number; isLatestSeason: boolean }[]>([]);
    const cellValuesChangedRef = useRef<{ field: string; kpi: string }[]>([]);
    const debounceCalcTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const debounceEmitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onWeekDataChangedRef = useRef(onWeekDataChanged);
    onWeekDataChangedRef.current = onWeekDataChanged;
    const isProgrammaticUpdate = useRef(false);
    const workspaceContainerRef = useRef<HTMLDivElement>(null);
    const regionKpiConfigRef = useRef<CalculatorConfig[]>([]);
    const regionGroundKpiConfigRef = useRef<CalculatorConfig[]>([]);

    // Selection & clipboard refs (stable across renders)
    const selectionHelperRef = useRef(new GridSelectionHelper());
    const localClipboardRef = useRef(new ClipboardService(15));
    const clipboardServiceRef = clipboardServiceProp
        ? { current: clipboardServiceProp }
        : localClipboardRef;

    // Effective KPI configs (with region expansion)
    const kpiConfig = kpiConfigProp ?? planKpiCalculatorConfig_CoreExtendedPlannedArticleEnabled;
    const groundKpiConfig = groundKpiConfigProp ?? groundKpiCalculatorConfig_OrderedGroundEnabled;

    // Selected plan / ground KPIs (from parent or defaults)
    const selectedPlanKpis = selectedPlanKpisProp ?? DEFAULT_PLAN_KPIS;
    const selectedGroundKpis = selectedGroundKpisProp ?? DEFAULT_GROUND_KPIS;

    // ── Base column defs (pinned KPI + market columns) ───────────────
    const baseColDefs: (ColDef | ColGroupDef)[] = useMemo(() => [
        {
            headerName: '',
            field: 'marketShortName',
            spanRows: true,
            suppressSpanHeaderHeight: false,
            pinned: 'left',
            cellClass: ['kpi-vertical-col-style', 'market-col-border'],
            headerClass: 'empty-market-header',
            resizable: false,
            sortable: false,
            suppressMovable: true,
            width: 30,
            minWidth: 30,
            maxWidth: 30,
            suppressSizeToFit: true,
            tooltipValueGetter: (param: { value?: unknown }) => `Market: ${param.value}`,
        } as ColDef,
        {
            headerName: 'H&M Quarter',
            minWidth: 85,
            children: [{
                headerName: TIME_PERIOD,
                minWidth: 50,
                children: [{
                    headerName: TIME_WEEK,
                    field: 'kpi',
                    minWidth: 85,
                    resizable: false,
                    suppressMovable: true,
                    sortable: false,
                    pinned: 'left',
                    tooltipValueGetter: (param: { data?: Record<string, unknown>; value?: unknown }) => {
                        if ((param.data?.id as string | undefined)?.startsWith('AI__')) return param.value as string;
                        if (param.data?.timeLevel === TIME_QUARTER || param.data?.timeLevel === TIME_PERIOD) {
                            return `${param.value}: plan (gnd)`;
                        }
                        return param.value as string;
                    },
                    cellClass: (param: { data?: Record<string, unknown>; value?: unknown }) => {
                        const classes: string[] = [];
                        if (isExcitementKpiRow(param) && !isIncludeExcitementToggleChecked) {
                            classes.push('excitement-kpi-col-style');
                        } else {
                            classes.push('kpi-col-style');
                        }
                        if (param.value === 'Sum Fin Q') classes.push('thick-border-top');
                        return classes;
                    },
                }],
            }],
        },
    ], [isIncludeExcitementToggleChecked]);

    // ── 1. Fetch timeline config ─────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        GridDataService.fetchTimeConfig().then(() => {
            if (!cancelled) {
                GridDataService.ensureTimelineMap();
                setTimelineReady(true);
            }
        });
        return () => { cancelled = true; };
    }, []);

    // ── Set department info in GridDataService ────────────────────────
    useEffect(() => {
        GridDataService.setSelectedDepartmentInfo(selectedDepartmentInfo ?? null);
    }, [selectedDepartmentInfo]);

    // ── Shared debounced calculator + emit logic ──────────────────────
    const scheduleDebouncedCalcAndEmit = useCallback(() => {
        // Debounced calculator execution (800ms)
        if (debounceCalcTimer.current) clearTimeout(debounceCalcTimer.current);
        debounceCalcTimer.current = setTimeout(() => {
            const events = cellValuesChangedRef.current;
            cellValuesChangedRef.current = [];

            // Re-initialize with this workspace's market-specific config before running
            CalculatorService.initializeCalculators(regionKpiConfigRef.current);
            CalculatorService.initializeGroundCalculators(regionGroundKpiConfigRef.current);

            // Run calculator – modifies rowDataMap objects in place
            CalculatorService.processGridEditEvents(events, rowDataMapRef.current, false);

            // Force grid to re-read and display updated cell values
            gridApiRef.current?.refreshCells({ force: true, suppressFlash: true });
        }, 800);

        // Debounced emit to parent (2000ms)
        if (debounceEmitTimer.current) clearTimeout(debounceEmitTimer.current);
        debounceEmitTimer.current = setTimeout(() => {
            const events = cellValuesStateRef.current;
            cellValuesStateRef.current = [];
            const apiState = new Map<string, { value: number; isLatestSeason: boolean }>();
            const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();
            const weekToSeasonMap = GridDataService.getWeekToSeasonMap();

            for (const ev of events) {
                const weekNumber = cellFieldNameToWeekMap.get(ev.field);
                if (weekNumber) {
                    const seasonNumbers = weekToSeasonMap.get(weekNumber) ?? [];
                    const seasonKey = ev.isLatestSeason ? seasonNumbers.at(-1) : seasonNumbers[0];
                    apiState.set(`${ev.kpi}__${seasonKey}__${weekNumber}`, {
                        value: ev.value,
                        isLatestSeason: ev.isLatestSeason,
                    });
                }
            }
            onWeekDataChangedRef.current?.(apiState);
        }, 2000);
    }, []);

    // ── Handle tooltip value changes (multi-season tooltip edits) ────
    const handleTooltipChange = useCallback(
        (data: { field: string; isLatestSeason: boolean; kpi: string; value: number; isMainMarket: boolean }) => {
            // Update the specific row in rowDataMap
            const rowData = rowDataMapRef.current.get(data.kpi);
            if (rowData) {
                if (data.isLatestSeason) {
                    rowData[data.field] =
                        Number(data.value ?? 0) + Number(rowData[data.field + '_es'] ?? 0);
                } else {
                    rowData[data.field] =
                        Number(data.value ?? 0) +
                        Number(rowData[data.field] ?? 0) -
                        Number(rowData[data.field + '_es'] ?? 0);
                }
            }

            // Track changed state
            cellValuesStateRef.current.push({
                field: data.field,
                kpi: data.kpi,
                value: Number(data.value),
                isLatestSeason: data.isLatestSeason,
            });

            cellValuesChangedRef.current.push({ field: data.field, kpi: data.kpi });

            scheduleDebouncedCalcAndEmit();
        },
        [scheduleDebouncedCalcAndEmit],
    );

    // ── 2. Build column defs once timeline is ready ──────────────────
    useEffect(() => {
        if (!timelineReady) return;
        const defs = GridDataService.getColumnDefinitionsForTimeline(
            readonlyPlan,
            [...baseColDefs],
            isIncludeExcitementToggleChecked,
            handleTooltipChange,
        );
        setColDefs(defs);
    }, [timelineReady, readonlyPlan, baseColDefs, isIncludeExcitementToggleChecked, handleTooltipChange]);

    // ── Region-expanded KPI configs ──────────────────────────────────
    const regionKpiConfig = useMemo(() => {
        const expanded: CalculatorConfig[] = [];
        for (const region of selectedRegions) {
            for (const cfg of kpiConfig) {
                expanded.push({
                    ...cfg,
                    kpiKey: cfg.kpiKey + '__' + region.marketCode,
                    dependentKpiKeys: cfg.dependentKpiKeys.map((k) => k + '__' + region.marketCode),
                    marketCode: region.marketCode,
                });
            }
        }
        return expanded;
    }, [kpiConfig, selectedRegions]);

    const regionGroundKpiConfig = useMemo(() => {
        const expanded: CalculatorConfig[] = [];
        for (const region of selectedRegions) {
            for (const cfg of groundKpiConfig) {
                expanded.push({
                    ...cfg,
                    kpiKey: cfg.kpiKey + '__' + region.marketCode,
                    dependentKpiKeys: cfg.dependentKpiKeys.map((k) => k + '__' + region.marketCode),
                });
            }
        }
        return expanded;
    }, [groundKpiConfig, selectedRegions]);

    // Keep refs in sync so debounced callbacks always use correct market-specific config
    useEffect(() => { regionKpiConfigRef.current = regionKpiConfig; }, [regionKpiConfig]);
    useEffect(() => { regionGroundKpiConfigRef.current = regionGroundKpiConfig; }, [regionGroundKpiConfig]);

    // ── Format initial grid data ─────────────────────────────────────
    const formatInitialGridData = useCallback((): Record<string, unknown>[] => {
        if (!initialGridData?.departmentLevelKpiPlanModels) return [];
        const weekToCellFieldNameMap = GridDataService.getWeekToCellFieldNameMap();
        const weekToSeasonMap = GridDataService.getWeekToSeasonMap();
        const data: Record<string, unknown>[] = [];

        const kpiIds = [
            { id: KPI_SQ, isCalculated: true },
            { id: KPI_SP, isCalculated: true },
            { id: KPI_AI, isCalculated: true },
            { id: KPI_SW, isCalculated: true },
            { id: KPI_CORE, isCalculated: false },
            { id: KPI_E, isCalculated: false },
            { id: KPI_EXT, isCalculated: false },
            { id: KPI_ART, isCalculated: false },
        ];

        const ids = new Set(
            kpiIds.filter((item) => item.isCalculated || selectedPlanKpis.includes(item.id)).map((item) => item.id),
        );

        for (const region of selectedRegions) {
            // Determine if this region is connected to main market (should be read-only)
            const isConnected = connectedMarketCodes?.has(region.marketCode) ?? false;
            const sourceMarketCode = isConnected ? mainMarketCode : undefined;

            for (const id of ids) {
                const kpidata = initialGridData.departmentLevelKpiPlanModels.filter((m) => m.id === id);
                if (kpidata.length !== 0) {
                    createRowData(kpidata, data, region, weekToCellFieldNameMap, sourceMarketCode, weekToSeasonMap);
                }
            }
        }
        return data;
    }, [initialGridData, selectedRegions, selectedPlanKpis, connectedMarketCodes, mainMarketCode]);

    // ── Format ground row data ───────────────────────────────────────
    const formatGroundRowGridData = useCallback(
        (baseData: Record<string, unknown>[]): Record<string, unknown>[] => {
            if (!groundRowData.length) return baseData;
            const weekToCellFieldNameMap = GridDataService.getWeekToCellFieldNameMap();
            const data: Record<string, unknown>[] = [];

            const groundKpiIds = [
                KPI_CORE_GROUND, KPI_E_GROUND, KPI_EXT_GROUND,
                KPI_UNKNOWN_GROUND, KPI_ORDERED_GROUND,
            ];
            const ids = new Set(groundKpiIds.filter((id) => selectedGroundKpis.includes(id)));

            for (const region of selectedRegions) {
                // Determine if this region is connected to main market (should be read-only)
                const isConnected = connectedMarketCodes?.has(region.marketCode) ?? false;
                const sourceMarketCode = isConnected ? mainMarketCode : undefined;

                const selectedRegionData = baseData
                    .filter((m) => m.marketShortName === region.marketShortName)
                    .map((r) => ({ ...r }));

                for (const id of ids) {
                    const kpidata = groundRowData.filter((m) => m.id === id);
                    if (kpidata.length !== 0) {
                        createRowData(kpidata as unknown as DepartmentLevelKpiPlanModels[], selectedRegionData, region, weekToCellFieldNameMap, sourceMarketCode, GridDataService.getWeekToSeasonMap());
                    }
                }
                data.push(...selectedRegionData);
            }
            return data;
        },
        [groundRowData, selectedRegions, selectedGroundKpis, connectedMarketCodes, mainMarketCode],
    );

    // ── Execute calculators on initial data ──────────────────────────
    const executeKpiCalculatorsOnInitialGridData = useCallback(
        (formattedData: Record<string, unknown>[]): Record<string, unknown>[] => {
            const map = new Map<string, Record<string, unknown>>();
            for (const row of formattedData) map.set(row.id as string, row);
            rowDataMapRef.current = map;

            const cellInfoList: { field: string; kpi: string }[] = [];
            const nonCalc = formattedData.filter((d) => !d.isCalculated);
            for (const kpi of nonCalc) {
                const weeklyKeys = Object.keys(kpi).filter(
                    (k) => k.startsWith(Q) && kpi[k] != null && kpi[k] !== '',
                );
                for (const wk of weeklyKeys) cellInfoList.push({ field: wk, kpi: kpi.id as string });
            }
            return CalculatorService.processGridEditEvents(cellInfoList, map, true);
        },
        [],
    );

    // ── Master refresh: format → calculate → filter → set ────────────
    const refreshGridData = useCallback(() => {
        if (!timelineReady || !initialGridData) return;

        CalculatorService.initializeCalculators(regionKpiConfig);
        CalculatorService.initializeGroundCalculators(regionGroundKpiConfig);

        let data = formatInitialGridData();
        data = formatGroundRowGridData(data);
        data = executeKpiCalculatorsOnInitialGridData(data);

        // Populate rowDataMap with ALL rows (including ground) so the
        // debounced calculator can always resolve ground KPI data when
        // recalculating period / quarter sums after a cell edit.
        rowDataMapRef.current.clear();
        data.forEach((row) => {
            if (row.id) {
                rowDataMapRef.current.set(row.id as string, row);
            }
        });

        // Apply ground articles filter (display only – rowDataMapRef keeps all rows)
        if (!isShowGroundArticlesToggleChecked) {
            data = data.filter((row) => row.planLevel !== PLANLEVEL_GROUND);
        }

        data.forEach((row, index) => {
            row.isFirstInRegion = index === 0 || row.marketShortName !== data[index - 1].marketShortName;
        });

        setRowData(data);
    }, [
        timelineReady, initialGridData,
        regionKpiConfig, regionGroundKpiConfig,
        formatInitialGridData, formatGroundRowGridData,
        executeKpiCalculatorsOnInitialGridData,
        isShowGroundArticlesToggleChecked,
    ]);

    // ── Trigger refresh when data or config changes ──────────────────
    useEffect(() => {
        refreshGridData();
    }, [refreshGridData]);

    // ── Grid ready ───────────────────────────────────────────────────
    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
        selectionHelperRef.current.setGridApi(params.api);
        selectionHelperRef.current.setSelectionChangeCallback((count, sum) => {
            setGridSelectionData({ cellsCount: count, sum });
        });
    }, [setGridSelectionData]);

    // ── Drag-select mouse handlers ───────────────────────────────────
    const onCellMouseDown = useCallback((event: CellMouseDownEvent) => {
        if (event.rowIndex == null || !event.event || !(event.event instanceof MouseEvent)) return;
        selectionHelperRef.current.handleCellMouseDown({
            event: event.event,
            rowIndex: event.rowIndex,
            column: event.column,
        });
    }, []);

    const onCellMouseOver = useCallback((event: CellMouseOverEvent) => {
        if (event.rowIndex == null) return;
        selectionHelperRef.current.handleCellMouseOver({
            rowIndex: event.rowIndex,
            column: event.column,
        });
    }, []);

    // Global mouseup to finalise drag-select
    useEffect(() => {
        const handler = () => { selectionHelperRef.current.handleCellMouseUp(); };
        document.addEventListener('mouseup', handler);
        return () => { document.removeEventListener('mouseup', handler); };
    }, []);

    // ── Copy / Paste / Undo handlers ─────────────────────────────────
    const handleCopy = useCallback(() => {
        const api = gridApiRef.current;
        const state = selectionHelperRef.current.getState();
        if (!api || state.selectedCells.size === 0) return;

        const count = clipboardServiceRef.current.copy(
            state.selectedCells, api, !!_isMainMarket, GridDataService.getWeekToSeasonMap(),
        );

        if (count === -1) {
            addToast({ title: 'Warning', subtitle: 'Please select cells within the same time range (week, period, or quarter)', type: 'warning', timeout: 5000 });
        } else if (count > 0) {
            addToast({ title: 'Copied', subtitle: `Copied ${count} cell${count > 1 ? 's' : ''}`, type: 'success', timeout: 5000 });
        }
    }, [_isMainMarket, addToast]);

    const handlePaste = useCallback(() => {
        const api = gridApiRef.current;
        if (!api || readonlyPlan) return;

        const focusedCell = api.getFocusedCell();
        if (!focusedCell) {
            addToast({ title: 'Warning', subtitle: 'Please click on a cell to set the paste position', type: 'warning', timeout: 5000 });
            return;
        }
        const focusedCellKey = `${focusedCell.rowIndex}_${focusedCell.column.getColId()}`;
        const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();

        const result = clipboardServiceRef.current.paste(
            focusedCellKey, api, !!_isMainMarket,
            GridDataService.getWeekToSeasonMap(), cellFieldNameToWeekMap,
        );

        if (result?.pastedCount === -1) {
            addToast({ title: 'Warning', subtitle: 'Cannot paste to read-only cells', type: 'warning', timeout: 5000 });
            return;
        }
        if (!result || result.pastedCount === 0) return;

        const changedCells = applyPastedDataToGrid(
            api, result.updatedData, cellFieldNameToWeekMap, GridDataService.getWeekToSeasonMap(),
        );

        api.forEachNode((node) => { if (node.data?.id) rowDataMapRef.current.set(node.data.id as string, node.data); });

        const fieldKeys = Array.from(result.updatedData.values()).map((d) => ({
            field: d.field,
            kpi: api.getDisplayedRowAtIndex(d.rowIndex)?.data?.id as string ?? '',
        }));
        CalculatorService.initializeCalculators(regionKpiConfigRef.current);
        CalculatorService.initializeGroundCalculators(regionGroundKpiConfigRef.current);
        CalculatorService.processGridEditEvents(fieldKeys, rowDataMapRef.current, false);

        if (changedCells.size > 0) onWeekDataChanged?.(changedCells);
        api.refreshCells({ force: true, suppressFlash: true });
        addToast({ title: 'Pasted', subtitle: `Pasted ${result.pastedCount} cell${result.pastedCount > 1 ? 's' : ''}`, type: 'success', timeout: 5000 });
    }, [readonlyPlan, _isMainMarket, addToast, onWeekDataChanged]);

    const handleUndo = useCallback(() => {
        const action = clipboardServiceRef.current.undo();
        if (!action) return;
        if (action.isMainMarket !== !!_isMainMarket) return;

        const api = gridApiRef.current;
        if (!api) return;

        const previous = clipboardServiceRef.current.getPreviousState(action);
        const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();
        const changedCells = applyUndoCellsToGrid(
            api, previous, cellFieldNameToWeekMap, GridDataService.getWeekToSeasonMap(),
        );

        api.forEachNode((node) => { if (node.data?.id) rowDataMapRef.current.set(node.data.id as string, node.data); });

        const fieldKeys = action.affectedFields.map((field) => ({
            field,
            kpi: api.getDisplayedRowAtIndex(action.affectedRows[0])?.data?.id as string ?? '',
        }));
        CalculatorService.initializeCalculators(regionKpiConfigRef.current);
        CalculatorService.initializeGroundCalculators(regionGroundKpiConfigRef.current);
        CalculatorService.processGridEditEvents(fieldKeys, rowDataMapRef.current, false);

        if (changedCells.size > 0) onWeekDataChanged?.(changedCells);
        api.refreshCells({ force: true, suppressFlash: true });
        addToast({ title: 'Undo', subtitle: 'Undo completed', type: 'success', timeout: 5000 });
    }, [_isMainMarket, addToast, onWeekDataChanged]);

    // ── Context menu handlers ────────────────────────────────────────
    const hideContextMenu = useCallback(() => {
        setShowContextMenu(false);
        contextMenuCellRef.current = null;
    }, []);

    const onGridContextMenu = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        if (!_isMainMarket) return;

        const cellElement = (event.target as HTMLElement).closest('.ag-cell') as HTMLElement;
        if (!cellElement) return;
        const colId = cellElement.getAttribute('col-id');
        const rowElement = cellElement.closest('[row-index]');
        const rowIndex = rowElement ? Number.parseInt(rowElement.getAttribute('row-index') ?? '0') : null;
        if (rowIndex === null || !colId) return;

        const api = gridApiRef.current;
        if (!api) return;
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);
        const column = api.getColumn(colId);
        if (!rowNode || !column) return;

        // Skip non-data columns
        if (colId === 'kpi' || colId === 'marketShortName') return;
        const kpi = rowNode.data?.kpi as string | undefined;
        if (kpi?.includes('Sum') || kpi?.includes(TIME_QUARTER) || kpi?.includes('Fin Q')) return;
        if (readonlyPlan || rowNode.data?.isCalculated) return;
        if (rowNode.data?.isConnectedToMainMarket) return; // connected markets are read-only
        if (rowNode.data?.planLevel === PLANLEVEL_GROUND) return; // ground rows should not be copyable

        const cellFieldNameToWeekMap = GridDataService.getCellFieldNameToWeekMap();
        const week = cellFieldNameToWeekMap.get(colId);
        const timeline = GridDataService.getTimelineMap();
        const currentWeek = timeline?.currentWeek ?? '';
        if (!week || Number(week) < Number(currentWeek)) return;

        contextMenuCellRef.current = { node: rowNode, column, value: rowNode.data[colId] };
        setContextMenuPos({ x: event.clientX, y: event.clientY });
        setShowContextMenu(true);
    }, [_isMainMarket, readonlyPlan]);

    const onSetArticlePlanToAllRegions = useCallback(() => {
        const api = gridApiRef.current;
        if (!api || !contextMenuCellRef.current) return;

        const selectionState = selectionHelperRef.current.getState();
        const kpiData = buildCopyToAllRegionsData({
            gridApi: api,
            selectedCells: selectionState.selectedCells,
            selectedCellsCount: selectionState.selectedCellsCount,
            currentContextMenuCell: contextMenuCellRef.current,
            selectedRegions,
            isStoreWorkspaceTab: !!_isStoreWorkspaceTab,
            selectedDepartmentInfo: selectedDepartmentInfo ?? null,
            readonlyPlan,
        });

        if (kpiData) {
            onCopyToAllRegions?.(kpiData);
        } else {
            addToast({ title: 'Warning', subtitle: 'No valid article data cells found for copying', type: 'warning', timeout: 5000 });
        }
        hideContextMenu();
    }, [selectedRegions, _isStoreWorkspaceTab, selectedDepartmentInfo, readonlyPlan, onCopyToAllRegions, addToast, hideContextMenu]);

    // ── Cell value changed → debounced calc + emit ───────────────────
    const onCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            // Ignore events triggered by programmatic grid updates
            if (isProgrammaticUpdate.current) {
                return;
            }

            // Ignore if value hasn't actually changed
            if (event.oldValue === event.newValue) {
                return;
            }

            const api = event.api;
            gridApiRef.current = api;

            // Don't update rowDataMap from grid nodes - it may not include all rows due to virtualization
            // Instead, just update the specific row that changed
            const field = event.colDef.field ?? '';
            const kpiId = event.data.id as string;

            // Update the specific row in rowDataMap
            const existingRow = rowDataMapRef.current.get(kpiId);
            if (existingRow) {
                existingRow[field] = event.newValue;
                rowDataMapRef.current.set(kpiId, existingRow);
            } else {
                // Row doesn't exist in map yet, add it
                rowDataMapRef.current.set(kpiId, event.data);
            }

            // Track changed state
            cellValuesStateRef.current.push({
                field,
                kpi: kpiId,
                value: event.newValue as number,
                isLatestSeason: event.data.isLatestSeason as boolean,
            });

            // Clear ending-season value on direct edit
            if (event.data[field + '_es'] != null) {
                event.data[field + '_es'] = undefined;
                cellValuesStateRef.current.push({ field, kpi: kpiId, value: 0, isLatestSeason: false });
            }

            cellValuesChangedRef.current.push({ field, kpi: kpiId });

            scheduleDebouncedCalcAndEmit();
        },
        [scheduleDebouncedCalcAndEmit],
    );

    // ── Arrow key navigation ─────────────────────────────────────────
    const onKeyDown = useCallback((event: React.KeyboardEvent) => {
        // Ctrl+C / Ctrl+V / Ctrl+Z shortcuts
        const isCtrlOrCmd = event.ctrlKey || event.metaKey;
        if (isCtrlOrCmd && event.key === 'c') {
            event.preventDefault();
            handleCopy();
            return;
        }
        if (isCtrlOrCmd && event.key === 'v') {
            event.preventDefault();
            handlePaste();
            return;
        }
        if (isCtrlOrCmd && event.key === 'z') {
            event.preventDefault();
            handleUndo();
            return;
        }

        const target = event.target as HTMLInputElement;
        if (target.type !== 'number') return;
        const api = gridApiRef.current;
        if (!api) return;
        const cell = api.getFocusedCell();
        if (!cell) return;

        const gridKeys = GridDataService.getGridRowDataKeys();
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
            const rowIndex = event.code === 'ArrowUp'
                ? Math.max(0, cell.rowIndex - 1)
                : cell.rowIndex + 1;
            api.setFocusedCell(rowIndex, cell.column.getColId());
        } else if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
            const colId = cell.column.getColId();
            const idx = gridKeys.indexOf(colId);
            const newIdx = event.code === 'ArrowLeft'
                ? Math.max(0, idx - 1)
                : Math.min(gridKeys.length - 1, idx + 1);
            api.setFocusedCell(cell.rowIndex, gridKeys[newIdx]);
        }
    }, [handleCopy, handlePaste, handleUndo]);

    // ── Resize handler ────────────────────────────────────────────────
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const handler = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                gridApiRef.current?.sizeColumnsToFit();
            }, 150);
        };
        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('resize', handler);
            if (timer) clearTimeout(timer);
        };
    }, []);

    // ── Handle double-click to trigger multi-season tooltip (matches Angular) ───
    // Angular: this.dataTransferService.isCellDoubledClicked.next(true);
    // Close all existing tooltips first, then open the one for the clicked field
    const handleCellDoubleClicked = useCallback((event: CellDoubleClickedEvent) => {
        const field = event.column?.getColId();
        
        // Cancel any drag selection that may have started from the first click
        selectionHelperRef.current.cancelDrag();
        
        // First, close all existing tooltips
        document.dispatchEvent(new CustomEvent(MST_DOUBLE_CLICK_EVENT, { detail: { show: false, field: null } }));
        
        // Then open the tooltip for the double-clicked cell's field
        // Small delay ensures the close event is processed first
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent(MST_DOUBLE_CLICK_EVENT, { detail: { show: true, field } }));
        }, 0);
        
        onTooltipTriggered?.();
    }, [onTooltipTriggered]);

    // ── Clear cell selection on click outside ─────────────────────────
    useEffect(() => {
        const appRoot = document.getElementById('root');

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // Ignore clicks on body-level portals (BADS dropdowns, modals, toasts)
            // so that AppMenu and other overlays remain interactive.
            if (appRoot && !appRoot.contains(target)) return;

            if (workspaceContainerRef.current && !workspaceContainerRef.current.contains(target)) {
                gridApiRef.current?.clearFocusedCell();
                selectionHelperRef.current.clearSelection();
                hideContextMenu();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [hideContextMenu]);

    // ── Cleanup debounce timers ───────────────────────────────────────
    useEffect(() => {
        return () => {
            if (debounceCalcTimer.current) clearTimeout(debounceCalcTimer.current);
            if (debounceEmitTimer.current) clearTimeout(debounceEmitTimer.current);
        };
    }, []);

    // ── Format published info helpers ──────────────────────────────────
    const formatPublishedInfo = (publishedTimeUtc: string, publishedBy?: string): string => {
        const publishedTime = new Date(publishedTimeUtc).toLocaleString();
        if (publishedBy) {
            return `${publishedTime} by ${publishedBy}`;
        }
        return publishedTime;
    };

    const getDepartmentLastPublishedTime = (): string => {
        const metadata = _isStoreWorkspaceTab
            ? departmentLastPublishedInfo?.storeMetadataResultModel
            : departmentLastPublishedInfo?.onlineMetadataResultModel;

        if (!metadata?.publishedTimeUtc) {
            return 'Yet to be Published';
        }

        return formatPublishedInfo(metadata.publishedTimeUtc, metadata.publishedBy);
    };

    // ── Cell class rules for selection highlighting ─────────────────
    const defaultColDef = useMemo<ColDef>(() => ({
        cellClassRules: {
            'selected-cell': (params) => {
                const ri = params.node?.rowIndex;
                const field = params.colDef?.field ?? params.colDef?.colId ?? params.column?.getColId?.() ?? '';
                if (ri == null || !field) return false;
                return selectionHelperRef.current.getState().selectedCells.has(`${ri}_${field}`);
            },
        },
    }), []);

    // ── onCellClicked – hide context menu ─────────────────────────────
    const onCellClicked = useCallback(() => { hideContextMenu(); }, [hideContextMenu]);

    const getSectionLastPublishedTime = (): React.ReactNode => {
        if (!sectionLastPublishedInfo?.metadata || sectionLastPublishedInfo.metadata.length === 0) {
            return 'Yet to be Published';
        }

        return (
            <div style={{ textAlign: 'left' }}>
                {sectionLastPublishedInfo.metadata.map((dept, index) => {
                    const savedTime = dept.savedTimeUtc
                        ? new Date(dept.savedTimeUtc).toLocaleDateString()
                        : '            ';

                    let publishedInfo = '';
                    if (dept.publishedTimeUtc && dept.publishedBy) {
                        publishedInfo = `${new Date(dept.publishedTimeUtc).toLocaleDateString()} by ${dept.publishedBy}`;
                    } else if (dept.publishedTimeUtc) {
                        publishedInfo = new Date(dept.publishedTimeUtc).toLocaleDateString();
                    }

                    return (
                        <div key={`${dept.departmentId}-${index}`}>
                            {dept.departmentNumber}; Last saved: {savedTime}; Last published: {publishedInfo}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────
    if (!timelineReady || colDefs.length === 0) return null;

    // Extract market name(s) from selectedRegions
    const marketName = selectedRegions[0]?.marketShortName || '';
    const marketLabel = _isMainMarket ? 'Main market' : 'Market';

    return (
        <div
            ref={workspaceContainerRef}
            className="workspaceArea"
            onKeyDown={onKeyDown}
            role="grid"
            tabIndex={0}
        >
            <div className="main-market-display">
                {_isMainMarket && (<Label forId="main-marketName" labelSize="normal">
                    {marketLabel}: {marketName}
                </Label>)}

                <span className="saving-indicator" style={{ visibility: isSaving ? 'visible' : 'hidden' }}>
                    <Spinner size="small" color='default' />
                    <Label forId="saving-indicator" labelSize="normal">
                        {_isMainMarket ? 'Saving main market...' : 'Saving non main market...'}
                    </Label>
                </span>

                {/* Recovery banner when grid state changed during save */}
                {_isMainMarket && isGridStateChanged && (
                    <span className="grid-state-changed-message">
                        <Label forId="grid-state-changed-label" labelSize="normal">
                            Grid data has changed since last loaded. Please click confirm to re-apply your last changes!
                        </Label>
                        <Button 
                            mode="primary" 
                            size="small"
                            onClick={onConfirmReapply}
                        >
                            Confirm
                        </Button>
                    </span>
                )}

                {_isMainMarket && showPublishedInfo && departmentLastPublishedInfo && (
                    <div className="publish-info-display">
                        <Tooltip mode='secondary' content={getDepartmentLastPublishedTime()} position="left" delay={100}>
                            <Icons.Info style={{ cursor: 'pointer', width: '20px', height: '20px' }} />
                        </Tooltip>
                        <Label forId="last-published" labelSize="normal">Last published</Label>
                    </div>
                )}
                
                {_isMainMarket && showPublishedInfo && sectionLastPublishedInfo && (
                    <div className="publish-info-display">
                        <Tooltip mode='secondary' content={getSectionLastPublishedTime()} position="left" delay={100}>
                            <Icons.Info style={{ cursor: 'pointer', width: '20px', height: '20px' }} />
                        </Tooltip>
                        <Label forId="last-published" labelSize="normal">Last published</Label>
                    </div>
                )}
            </div>
            <div role="none" onContextMenu={onGridContextMenu}>
                <AgGridReact
                    theme={myTheme}
                    columnDefs={colDefs}
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                    onCellValueChanged={onCellValueChanged}
                    onCellDoubleClicked={handleCellDoubleClicked}
                    onCellMouseDown={onCellMouseDown}
                    onCellMouseOver={onCellMouseOver}
                    onCellClicked={onCellClicked}
                    domLayout="autoHeight"
                    singleClickEdit={false}
                    suppressClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                    suppressCellFocus={false}
                    animateRows={false}
                    suppressColumnVirtualisation
                    enableCellSpan
                    enableCellTextSelection={false}
                    rowClassRules={{ 'region-first-row': (params) => !!params.data?.isFirstInRegion }}
                    tooltipShowDelay={500}
                    tooltipHideDelay={10000}
                    tooltipMouseTrack={false}
                    tooltipInteraction={true}
                />
            </div>

            {/* Context menu */}
            {showContextMenu && (
                <div
                    className="context-menu"
                    style={{ top: contextMenuPos.y, left: contextMenuPos.x }}
                >
                    <button
                        className="context-menu-item"
                        type="button"
                        onClick={onSetArticlePlanToAllRegions}
                    >
                        Copy cells to non-main market
                    </button>
                </div>
            )}
        </div>
    );
}

// ── Helper: map cell changes to season-keyed entries for emission ──

function addSeasonChangesToMap(
    map: Map<string, { value: number; isLatestSeason: boolean }>,
    kpiId: string,
    weekNumber: string,
    seasonNumbers: string[],
    value: number,
    endingSeasonValue: number | null | undefined,
): void {
    if (endingSeasonValue == null) {
        const sn = seasonNumbers.at(-1) ?? seasonNumbers[0];
        if (sn) map.set(`${kpiId}__${sn}__${weekNumber}`, { value, isLatestSeason: true });
        return;
    }
    const oldSeason = seasonNumbers[0];
    if (oldSeason) map.set(`${kpiId}__${oldSeason}__${weekNumber}`, { value: endingSeasonValue, isLatestSeason: false });
    const latestSeason = seasonNumbers.at(-1);
    if (latestSeason) map.set(`${kpiId}__${latestSeason}__${weekNumber}`, { value: value - endingSeasonValue, isLatestSeason: true });
}

/** Apply pasted cell data to the grid and return a change map for emission. */
function applyPastedDataToGrid(
    api: GridApi,
    updatedData: Map<string, { rowIndex: number; field: string; value: number; endingSeasonValue?: number | null }>,
    cellFieldNameToWeekMap: Map<string, string>,
    weekToSeasonMap: Map<string, string[]>,
): Map<string, { value: number; isLatestSeason: boolean }> {
    const changedCells = new Map<string, { value: number; isLatestSeason: boolean }>();
    for (const [, cellData] of updatedData) {
        const rowNode = api.getDisplayedRowAtIndex(cellData.rowIndex);
        if (!rowNode?.data) continue;
        rowNode.data[cellData.field] = cellData.value;
        if (cellData.endingSeasonValue != null) rowNode.data[`${cellData.field}_es`] = cellData.endingSeasonValue;
        const weekNumber = cellFieldNameToWeekMap.get(cellData.field);
        if (weekNumber === undefined) continue;
        addSeasonChangesToMap(changedCells, rowNode.data.id as string, weekNumber, weekToSeasonMap.get(weekNumber) ?? [], cellData.value, cellData.endingSeasonValue);
    }
    return changedCells;
}

/** Apply undo state to the grid and return a change map for emission. */
function applyUndoCellsToGrid(
    api: GridApi,
    previous: Map<string, { value: number | null; endingSeasonValue?: number | null }>,
    cellFieldNameToWeekMap: Map<string, string>,
    weekToSeasonMap: Map<string, string[]>,
): Map<string, { value: number; isLatestSeason: boolean }> {
    const changedCells = new Map<string, { value: number; isLatestSeason: boolean }>();
    for (const [cellKey, cellState] of previous) {
        const parsed = parseCellKey(cellKey);
        if (!parsed) continue;
        const rowNode = api.getDisplayedRowAtIndex(parsed.rowIndex);
        if (!rowNode?.data) continue;
        rowNode.data[parsed.fieldName] = cellState.value;
        const weekNumber = cellFieldNameToWeekMap.get(parsed.fieldName);
        if (weekNumber === undefined) continue;
        const seasonNumbers = weekToSeasonMap.get(weekNumber) ?? [];
        const isMulti = seasonNumbers.length === 2;
        if (isMulti) rowNode.data[`${parsed.fieldName}_es`] = cellState.endingSeasonValue;
        const esValue = isMulti ? (cellState.endingSeasonValue ?? 0) : undefined;
        addSeasonChangesToMap(changedCells, rowNode.data.id as string, weekNumber, seasonNumbers, cellState.value ?? 0, esValue);
    }
    return changedCells;
}

// ── Helper: collect week data entries for a KPI row ───────────────

type WeekDataEntry = { id: string; weekWithYear: string; value: number; isLatestSeason: boolean };

function collectWeekData(
    kpidata: DepartmentLevelKpiPlanModels[],
    dataLookupId: string,
): WeekDataEntry[] {
    const weekData: WeekDataEntry[] = [];
    if (kpidata[0].isCalculated) {
        weekData.push(...(kpidata[0].data ?? []));
        if (kpidata[1]) weekData.push(...kpidata[1].data);
    } else {
        const filterById = (m: WeekDataEntry) =>
            m.id.split('__').slice(0, 2).join('__') === dataLookupId;
        weekData.push(...kpidata[0].data.filter(filterById));
        if (kpidata[1]) {
            weekData.push(...kpidata[1].data.filter(filterById));
        }
    }
    return weekData;
}

// ── Helper: assign week values to a row from collected week data ───

function assignWeekValues(
    row: Record<string, unknown>,
    weekData: WeekDataEntry[],
    weekToCellFieldNameMap: Map<string, string>,
    skipPreStartSeason: boolean,
    weekToSeasonMap?: Map<string, string[]>,
): void {
    const weekDataIds = new Set(weekData.map((d) => d.id));
    for (const id of weekDataIds) {
        const wdStarting = weekData.find((wd) => wd.id === id && wd.isLatestSeason);
        const wdEnding = weekData.find((wd) => wd.id === id && !wd.isLatestSeason);
        const weekWithYear = wdStarting?.weekWithYear ?? wdEnding?.weekWithYear ?? '';
        const fieldName = weekToCellFieldNameMap.get(weekWithYear);

        if (skipPreStartSeason && isNonPrimaryBeforeSplit(row, weekWithYear, weekToSeasonMap))
            continue;

        const stripEndingSeason = skipPreStartSeason
            && isNonPrimaryEndingSeasonBeforeSplit(row, weekWithYear, weekToSeasonMap);

        if (wdEnding && !stripEndingSeason) {
            row[fieldName + '_es'] = Number(wdEnding.value);
            if (fieldName) {
                row[fieldName] = Number((row[fieldName] as number) ?? 0) + Number(wdEnding.value);
            }
        }
        if (wdStarting && fieldName && Number(wdStarting.value) !== 0) {
            row[fieldName] = Number((row[fieldName] as number) ?? 0) + Number(wdStarting.value);
        }
    }
}

// ── Helper: create a formatted row from KPI data ──────────────────

function createRowData(
    kpidata: DepartmentLevelKpiPlanModels[],
    data: Record<string, unknown>[],
    selectedRegion: Market,
    weekToCellFieldNameMap: Map<string, string>,
    sourceMarketCode?: number,
    weekToSeasonMap?: Map<string, string[]>,
): void {
    const row: Record<string, unknown> = {};
    row.id = kpidata[0].id + '__' + selectedRegion.marketCode;
    row.isCalculated = kpidata[0].isCalculated;
    row.isLatestSeason = true;
    row.kpi = kpidata[0].kpi;
    row.timeLevel = kpidata[0].timeLevel;
    row.planLevel = kpidata[0].planLevel;
    row.marketShortName = selectedRegion.marketShortName;
    row.isMainMarket = selectedRegion.isMainMarket;
    row.marketCode = selectedRegion.marketCode;
    row.startSeasonNumber = selectedRegion.startSeasonNumber;
    row.isPlanningMarketMovedToRegion = selectedRegion.isPlanningMarketMovedToRegion;

    if (sourceMarketCode !== undefined) {
        row.isConnectedToMainMarket = true;
    }

    const dataLookupId = sourceMarketCode === undefined
        ? row.id as string
        : kpidata[0].id + '__' + sourceMarketCode;

    const weekData = collectWeekData(kpidata, dataLookupId);

    const skipPreStartSeason = sourceMarketCode !== undefined
        && !selectedRegion.isPlanningMarketMovedToRegion
        && !!selectedRegion.startSeasonNumber;

    assignWeekValues(row, weekData, weekToCellFieldNameMap, skipPreStartSeason, weekToSeasonMap);

    data.push(row);
}
